import { useCreateQuery, useGetProducts } from '../../backend/hooks';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import WhatsAppButton from '../../common/whatsapp';

export default function ContactUs() {
    const { mutate: createQuery, isPending: isLoading } = useCreateQuery();
    const { data: productsData, isLoading: productsLoading } = useGetProducts();
    const products = productsData?.products || [];

    const formik = useFormik({
        initialValues: {
            name: '',
            email: '',
            interested_in: 'Sensory Room Design',
            message: ''
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Name is required'),
            email: Yup.string().email('Invalid email address').required('Email is required'),
            interested_in: Yup.string(),
            message: Yup.string().required('Message is required')
        }),
        onSubmit: (values, { resetForm }) => {
            createQuery(values, {
                onSuccess: () => {
                    toast.success('Message sent! We’ll get back to you within 24 hours.', {
                        position: 'top-right',
                        autoClose: 4000
                    });
                    resetForm();
                },
                onError: (err) => {
                    toast.error(err.message || 'Failed to send message. Please try again.', {
                        position: 'top-right',
                        autoClose: 5000
                    });
                }
            });
        }
    });

    return (
        <main className="pt-24">
            <ToastContainer />
            <WhatsAppButton />
            {/* Hero Section */}
            <section className="w-full bg-[#15415c] py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h1 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight leading-tight">
                        Get in Touch
                    </h1>
                    <p className="mt-4 text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
                        Whether you're a parent seeking guidance or a hospital coordinator looking for bulk rehab equipment, our specialists are ready to assist.
                    </p>
                </div>
            </section>

            {/* Contact Info + Form */}
            <section className="py-16 md:py-24 bg-surface">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">
                        {/* Left column – Contact details */}
                        <div className="space-y-12">
                            <div>
                                <h2 className="text-3xl font-bold text-on-surface mb-4">Let’s Connect</h2>
                                <p className="text-on-surface-variant">
                                    Reach out to us using the details below or fill out the form and we’ll get back to you promptly.
                                </p>
                            </div>

                            <div className="space-y-8">
                                <div className="flex items-start gap-5">
                                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                                        <span className="material-symbols-outlined text-2xl">location_on</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-on-surface">Head Office</h4>
                                        <p className="text-on-surface-variant">Lahore, Pakistan</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-5">
                                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                                        <span className="material-symbols-outlined text-2xl">call</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-on-surface">Phone Support</h4>
                                        <p className="text-on-surface-variant">+92 314 4201005</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-5">
                                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                                        <span className="material-symbols-outlined text-2xl">mail</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-on-surface">Email Inquiries</h4>
                                        <p className="text-on-surface-variant">contact@kinetixpk.com</p>
                                    </div>
                                </div>
                            </div>

                            {/* Optional: Add social icons or map placeholder */}
                            <div className="pt-6 border-t border-outline-variant/20">
                                <p className="text-sm text-on-surface-variant">We’re here to help – reach out anytime.</p>
                            </div>
                        </div>

                        {/* Right column – Form */}
                        <div className="bg-surface-container-lowest p-8 md:p-12 rounded-3xl shadow-lg">
                            <h3 className="text-2xl font-bold font-headline mb-8">Send an Inquiry</h3>
                            <form onSubmit={formik.handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Full Name</label>
                                        <input
                                            type="text"
                                            name="name"
                                            placeholder="John Doe"
                                            className="w-full bg-surface-container-highest border-none rounded-xl px-5 py-4 focus:ring-2 focus:ring-primary focus:bg-surface-bright transition-all"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.name}
                                        />
                                        {formik.touched.name && formik.errors.name && (
                                            <div className="text-red-500 text-xs mt-1">{formik.errors.name}</div>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Email Address</label>
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder="john@example.com"
                                            className="w-full bg-surface-container-highest border-none rounded-xl px-5 py-4 focus:ring-2 focus:ring-primary focus:bg-surface-bright transition-all"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.email}
                                        />
                                        {formik.touched.email && formik.errors.email && (
                                            <div className="text-red-500 text-xs mt-1">{formik.errors.email}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">I am interested in...</label>
                                    <select
                                        name="interested_in"
                                        className="w-full bg-surface-container-highest border-none rounded-xl px-5 py-4 focus:ring-2 focus:ring-primary focus:bg-surface-bright transition-all"
                                        onChange={formik.handleChange}
                                        value={formik.values.interested_in}
                                    >
                                        <option value="">Select Your Interest</option>
                                        {productsLoading ? (
                                            <option value="" disabled>Loading products...</option>
                                        ) : (
                                            products.map((product) => (
                                                <option key={product.id} value={product.title}>
                                                    {product.title}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Message</label>
                                    <textarea
                                        name="message"
                                        rows="5"
                                        placeholder="How can we help you achieve your therapy goals?"
                                        className="w-full bg-surface-container-highest border-none rounded-xl px-5 py-4 focus:ring-2 focus:ring-primary focus:bg-surface-bright transition-all"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.message}
                                    />
                                    {formik.touched.message && formik.errors.message && (
                                        <div className="text-red-500 text-xs mt-1">{formik.errors.message}</div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full py-5 bg-gradient-to-r from-primary to-primary-container text-on-primary font-bold text-lg rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <span>Send Message</span>
                                            <span className="material-symbols-outlined">send</span>
                                        </>
                                    )}
                                </button>
                            </form>
                            <p className="text-center text-sm text-on-surface-variant mt-6">We aim to respond within 24 business hours.</p>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}